<template>
  <v-dialog
      v-model="dialog"
      hide-overlay
      fullscreen
  >
    <FullHeightRadialLoader :isLoading="isLoading" :loader-msg="$t('components:common.loading')"/>

    <v-card v-show="!isLoading" elevation="0">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog(true)">
          <v-icon large color="primary">close</v-icon>
        </v-btn>
      </v-col>

      <v-card-text>
        <v-form ref="form">
          <div class="pa-5">
            <h3>{{isEditMode ?
                $t('components:minimized-customer-form.title-edit') :
                $t('components:minimized-customer-form.title') }}</h3>

            <v-row justify="space-around">
              <v-col cols="12" md="5">
                <v-radio-group row v-model="customer.type" :rules="[ruleRequired($t('utils:rules.required'))]" tabindex="1">
                  <v-radio :label="$t('common:customer')" value="CUSTOMER" :disabled="suppliersOnlyEnabled"/>
                  <v-radio :label="$t('components:minimized-customer-form.labels.supplier')" value="SUPPLIER"/>
                </v-radio-group>

                <!-- Name -->
                <DuplicateCheckField
                    ref="duplName"
                    :value="customer.name"
                    :label="$t('common:name')"
                    item-additional-info-field="street"
                    required
                    tab-index="2"
                    item-text="name"
                    @input="customer.name = $event"
                    @checkForDuplicates="checkByName"
                    :trigger-focus="!isEditMode"
                    hide-show-btn
                />

                <v-text-field
                    outlined
                    v-model="customer.regNumber"
                    :label="$t('components:minimized-customer-form.labels.reg-number')"
                    clearable
                    tabindex="3"
                />

                <v-text-field
                    outlined
                    v-model="customer.vatId"
                    :label="$t('components:minimized-customer-form.labels.vatin')"
                    clearable
                    tabindex="4"
                />

                <h4 class="primary--text font-weight-bold">
                  {{ $t('common:address') }}
                </h4>
                <DuplicateCheckField
                    ref="duplAddress"
                    :value="customer.street"
                    :label="$t('common:street')"
                    item-additional-info-field="street"
                    tab-index="5"
                    item-text="name"
                    @input="customer.street = $event"
                    @checkForDuplicates="checkByAddress"
                    hide-show-btn
                />

                <!-- Input Select Country - set before zip for auto-fill-in of province/city from zip code-->
                <v-select
                    :items="restCountry"
                    item-text="translatedName"
                    item-value="id"
                    :menu-props="{ bottom: true, offsetY: true }"
                    v-model="customer.country"
                    outlined
                    :label="$t('common:country')"
                    clearable
                    @change="updateProvince"
                    tabindex="6"
                />

                <v-row justify="space-around">
                  <v-col cols="6" md="4">
                    <v-text-field
                        outlined
                        v-model="customer.zip"
                        :label="$t('common:zip')"
                        clearable
                        tabindex="7"
                        @input="getCityProvinceByZIP"
                    />
                  </v-col>
                  <v-col cols="6" md="8">
                    <v-text-field
                        outlined
                        v-model="customer.city"
                        :label="$t('common:city')"
                        clearable
                        tabindex="8"
                    />
                  </v-col>
                </v-row>

                <v-select
                    :items="restProvince"
                    item-text="translatedName"
                    item-value="id"
                    :menu-props="{ bottom: true, offsetY: true }"
                    v-model="customer.province"
                    outlined
                    :label="$t('common:province')"
                    clearable
                    tabindex="9"
                />
              </v-col>
              <v-col cols="12" md="5">
                <h4 class="primary--text font-weight-bold">
                  {{ $t('components:minimized-customer-form.contact') }}
                </h4>
                <v-text-field
                    outlined
                    v-model="customer.contactName"
                    :label="$t('components:minimized-customer-form.labels.contact-name')"
                    clearable
                    tabindex="10"
                />
                <v-text-field
                    outlined
                    v-model="customer.contactMail"
                    :label="$t('components:minimized-customer-form.labels.contact-mail')"
                    clearable
                    tabindex="11"
                />
                <v-text-field
                    outlined
                    v-model="customer.contactPhone"
                    :label="$t('components:minimized-customer-form.labels.contact-phone')"
                    clearable
                    tabindex="12"
                />

                <!-- only show banking account information for subCustomer, not in customerTypeAhead -->
                <div v-if="subCustomer">
                  <h4 class="primary--text font-weight-bold">
                    {{ $t('settings/customer:edit.labels.bank-account') }}
                  </h4>
                  <v-row justify="space-around">
                    <v-col cols="6" md="4">
                      <v-text-field
                          outlined
                          v-model="customer.bic"
                          :label="$t('settings/customer:edit.labels.bic')"
                          clearable
                          tabindex="13"
                      />
                    </v-col>
                    <v-col cols="6" md="8">
                      <v-text-field
                          outlined
                          v-model="customer.iban"
                          :label="$t('settings/customer:edit.labels.iban')"
                          clearable
                          tabindex="14"
                      />
                    </v-col>
                  </v-row>
                </div>

                <BaseButton
                    color="primary"
                    :btn-text="$t('buttons:save')"
                    @clickedButton="saveCustomer()"
                    tabindex="50"
                />

                <BaseButton
                    color="grey"
                    :btn-text="$t('buttons:back')"
                    @clickedButton="closeDialog(true)"
                    tabindex="51"
                />
              </v-col>
            </v-row>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Customer from "@/modules/core/data/customer";
import DuplicateCheckField from "@/modules/core/components/DuplicateCheckField";
import {ruleRequired} from "@/modules/core/services/rules";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import Utils from "@/modules/core/services/utils";
import CountryService from "@/modules/core/services/country-service";
import GPSUtils from "@/modules/core/services/gps-utils";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import CustomerService from "@/modules/core/services/customer-service";
import DataUtils from "@/modules/core/services/data-utils";
import FullHeightRadialLoader from "@/modules/core/components/Loader/FullHeightRadialLoader.vue";
export default {
  name: "MinimizedCustomerDialogForm.vue",
  components: {BaseButton, DuplicateCheckField, FullHeightRadialLoader},
  props: {
    subCustomer: Boolean,  // if subCustomer === true, parentId is set to customer of logged-in user
  },
  data() {
    return {
      dialog: false,
      customer: new Customer(),
      restCountry: [],
      restProvince: [],
      suppliersOnlyEnabled : false,
      isLoading: false,
      isEditMode: false,
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    }
  },
  methods: {
    closeDialog(emitClose = false) {
      this.dialog = false;
      this.$refs.form.reset();
      this.customer = new Customer(); // without reseting customer, there is a problem from edit-mode -> add-mode

      if (emitClose) {
        this.$emit('close')
      }
    },

    /**
     * public function to open edit dialog (called from SubCustomer)
     * @param subCustomer customer to edit
     */
    openEditDialog(subCustomer) {
      this.customer = subCustomer;
      this.updateProvince();
      this.openDialog(true);
    },

    /**
     * public function to open add dialog
     */
    openAddDialog(){
      this.openDialog(false);
    },

    /**
     * public function called from CustomerTypeAhead
     * @param name customer name
     */
    openDialogWithNameSupplierOnly(name) {
      this.customer.type = 'SUPPLIER';
      this.customer.name = name;
      this.suppliersOnlyEnabled = true;
      this.openDialog(false);
    },

    /**
     *  private function used for common code of open dialogs for add and edit
     * @param editMode boolean if is in edit mode
     */
    openDialog(editMode) {
      this.dialog = true;
      this.$nextTick(() =>  this.$refs.form.resetValidation() ); // this.$refs.form only available after next tick after
      this.isLoading = false;
      this.isEditMode = editMode;
      if (this.subCustomer && !this.customer.parentId) {
        //could not get parentId in created() so try again and show error if failed
        this.loadCustomerProfile(true);
      }
    },

    saveCustomer() {
      if (!this.$refs.form.validate()) {
        window.scrollTo(0, 0);
        return;
      }
      if (!this.isEditMode) {
        CustomerService.registerCustomer(this.customer,
            (res) => {
              this.$emit('onSave', res.data);
              this.closeDialog();
            },
            (error) => {
              this.$logger.error("Failed to add customer, because: " + error);
              Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:customer')}));
            }
        );
      }
      else {
        CustomerService.putToAPI(this.customer,
            (res) => {
              this.$emit('onSave', res.data);
              this.closeDialog();
            },
            (error) => {
              this.$logger.error("Failed to update customer, because: " + error);
              Utils.showFailureSnackbar(this.$t('snackbar:update-failure', {name: this.$t('common:customer')}));
            }
        );
      }
    },

    checkByName(term) {
      DuplicateCheckService.checkCustomers('NAME', term,
          (res) => {
            this.$refs.duplName.fillData(res.data, this.customer.id);
          }
      )
    },

    checkByAddress(term) {
      DuplicateCheckService.checkCustomers('ADDRESS', term,
          (res) => {
            this.$refs.duplAddress.fillData(res.data, this.customer.id);
          }
      )
    },

    translateCountry() {
      this.restCountry = Utils.translateAllCountries(this.restCountry);
    },

    translateProvinces() {
      this.restProvince = Utils.translateAllProvinces(this.restCountry, this.restProvince, this.customer.country);
    },

    getAllCountries() {
      CountryService.readAllFromAPI(
          (res) => {
            this.restCountry = res.data;
            this.translateCountry();

            if (this.customer.country == null) {
              this.customer.country = res.data[0].id;
              this.updateProvince();
            }
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:country')}))
            this.$logger.error(error);
          }
      )
    },

    updateProvince() {
      if (this.customer.country) {

        CountryService.readProvincesOfCountry(this.customer.country,
            (res) => {
              this.restProvince = res.data;
              this.translateProvinces();
            },
            (error) => {
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:province_plural')}))
              this.$logger.error(error);
            }
        )
      }
    },

    /**
     * Reads and sets the building city and province
     * by the entered ZIP and country
     */
    getCityProvinceByZIP() {
      // check if there is a zip and country set for the building
      if (this.customer.zip && this.customer.country) {

        const countryName = GPSUtils.getCountryNameById(this.customer.country, this.restCountry);

        if (GPSUtils.isValidZIP(countryName.toLowerCase(), this.customer.zip)) {
          GPSUtils.readCityProvinceByZIP(
              this.customer.zip,
              countryName,
              this.$store.state.translation.language,
              this.restProvince,
              (res) => {
                this.customer.city = res.city;
                this.customer.province = res.province;
              },
              () => {
                Utils.showWarningSnackbar(this.$t('settings/customer:edit.zip-to-city-fail'))
              }
          )
        }
      }
    },

    loadCustomerProfile(showFailAndCloseOnError) {
      if (this.subCustomer) {
        this.isLoading = true;
        CustomerService.readProfileFromAPI(
            (resp) => {
              let customerProfile = DataUtils.parseDataToSingleObject(resp.data, Customer);
              if (customerProfile && customerProfile.id) {
                this.customer.parentId = customerProfile.id;
              } else if (showFailAndCloseOnError) {
                Utils.showFailureSnackbar(this.$t('settings/profile:customer.user-management.snackbar.customer-load-failure'));
                this.closeDialog(true);
              }
              this.isLoading = false;
            },
            () => {
              if (showFailAndCloseOnError) {
                Utils.showFailureSnackbar(this.$t('settings/profile:customer.user-management.snackbar.customer-load-failure'));
                this.closeDialog(true);
                this.isLoading = false;
              }
            }
        );
      }
    },

    ruleRequired
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.getAllCountries();
        this.updateProvince();
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    }
  },
  created() {
    this.getAllCountries();
    this.loadCustomerProfile(false);
  }
}
</script>

<style scoped lang="scss">
.v-dialog__content.v-dialog__content--active {
  z-index: 500 !important;
}
</style>